// ErpRecordForm.tsx - 엔티티 명세로 그리는 등록/수정 폼 (기존 ERP 의 추가·수정 팝업 대체).
//
// 검증은 서버가 한다(`api/erp/validate.py`). 여기서는 필수값만 미리 막고, 422 의
// 필드별 메시지를 그 필드 아래에 붙인다. 첨부는 레코드가 생긴 뒤(수정 모드)에만 -
// 업로드 경로가 레코드 id 를 요구한다.
import {
  Button,
  Checkbox,
  ConfirmDialog,
  DateField,
  Dialog,
  Field,
  type FieldChildProps,
  Icon,
  Select,
  TextInput,
  Textarea,
} from "@/components/ui";
import {
  ApiError,
  createErpRecord,
  deleteErpFile,
  deleteErpRecord,
  getErpFileUrl,
  updateErpRecord,
  uploadErpFile,
} from "@/lib/api";
import {
  type Draft,
  type ErpEntity,
  type ErpField,
  type ErpRow,
  draftOf,
  editableFields,
  fieldErrors,
  isNumeric,
  missingRequired,
  payloadOf,
} from "@/lib/erp";
import { invalidateErpOptions, useErpOptions } from "@/lib/hooks";
import { type CSSProperties, useMemo, useState } from "react";

// 네이티브 month/time 입력 - ui.tsx 의 TextInput 은 이 타입을 받지 않는다.
const nativeStyle = (invalid?: boolean): CSSProperties => ({
  background: "var(--surface-inset)",
  color: "var(--text-primary)",
  border: `1px solid ${invalid ? "var(--state-late)" : "var(--border-strong)"}`,
});
const NATIVE = "w-full h-control-md rounded-control px-s4 text-label";

const RefSelect = ({
  entity,
  value,
  onChange,
  placeholder = "선택",
  ...aria
}: {
  entity: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
} & Partial<FieldChildProps> & { ariaLabel?: string }) => {
  const options = useErpOptions(entity);
  const opts = useMemo(
    () => options.map((o) => ({ value: o.id, label: o.label || o.id })),
    [options]
  );
  return (
    <Select value={value} onChange={onChange} options={opts} placeholder={placeholder} {...aria} />
  );
};

const FieldControl = ({
  f,
  value,
  onChange,
  p,
}: {
  f: ErpField;
  value: string | boolean;
  onChange: (v: string | boolean) => void;
  p: FieldChildProps;
}) => {
  const s = typeof value === "string" ? value : "";
  switch (f.type) {
    case "longtext":
      return <Textarea value={s} onChange={onChange} rows={3} {...p} />;
    case "date":
      return <DateField value={s} onChange={onChange} {...p} />;
    case "month":
    case "time":
      return (
        <input
          type={f.type}
          value={s}
          onChange={(e) => onChange(e.target.value)}
          className={NATIVE}
          style={nativeStyle(p["aria-invalid"])}
          data-num=""
          {...p}
        />
      );
    case "bool":
      return <Checkbox checked={value === true} onChange={onChange} label={f.label} />;
    case "enum":
      return <Select value={s} onChange={onChange} options={f.options} placeholder="선택" {...p} />;
    case "ref":
      return f.ref ? <RefSelect entity={f.ref} value={s} onChange={onChange} {...p} /> : null;
    default:
      return (
        <TextInput
          value={s}
          onChange={onChange}
          type="text"
          align={isNumeric(f.type) ? "right" : "left"}
          placeholder={f.auto ? "비우면 자동 채번" : f.unit || undefined}
          {...p}
        />
      );
  }
};

const FileList = ({
  entity,
  recordId,
  f,
  files,
  onChanged,
  onError,
}: {
  entity: string;
  recordId: string;
  f: ErpField;
  files: { key: string; filename: string }[];
  onChanged: (files: { key: string; filename: string }[]) => void;
  onError: (msg: string) => void;
}) => {
  const [busy, setBusy] = useState(false);
  const run = async (op: () => Promise<void>) => {
    setBusy(true);
    try {
      await op();
    } catch (e) {
      onError(
        e instanceof ApiError && e.status === 503
          ? "파일 저장소가 설정되지 않았습니다"
          : (e as Error).message
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-s2">
      {files.map((file) => (
        <div key={file.key} className="flex items-center gap-s2 text-label">
          <Icon name="paperclip" size={14} />
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            style={{ color: "var(--accent)" }}
            onClick={() =>
              void run(async () => {
                window.open(
                  await getErpFileUrl(entity, recordId, f.key, file.key),
                  "_blank",
                  "noopener"
                );
              })
            }
          >
            {file.filename}
          </button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            icon={<Icon name="trash" size={13} />}
            onClick={() =>
              void run(async () => {
                await deleteErpFile(entity, recordId, f.key, file.key);
                onChanged(files.filter((x) => x.key !== file.key));
              })
            }
          >
            <span className="sr-only">{file.filename} 삭제</span>
          </Button>
        </div>
      ))}
      <label
        className="inline-flex cursor-pointer items-center gap-s2 text-caption"
        style={{ color: "var(--text-secondary)" }}
      >
        <Icon name="plus" size={13} />
        파일 추가
        <input
          type="file"
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file)
              void run(async () => {
                const added = await uploadErpFile(entity, recordId, f.key, file);
                onChanged([...files, { key: added.key, filename: added.filename }]);
              });
          }}
        />
      </label>
    </div>
  );
};

export const ErpRecordForm = ({
  open,
  entity,
  row,
  canDelete,
  onClose,
  onSaved,
  toast,
}: {
  open: boolean;
  entity: ErpEntity;
  /** null = 새 레코드 */
  row: ErpRow | null;
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
  toast: (message: string, tone?: "success" | "error") => void;
}) => {
  const original = useMemo(() => (row ? draftOf(entity, row) : null), [entity, row]);
  const [draft, setDraft] = useState<Draft>(() => draftOf(entity, row));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [files, setFiles] = useState<Record<string, { key: string; filename: string }[]>>(() => {
    const out: Record<string, { key: string; filename: string }[]> = {};
    for (const f of entity.fields) {
      const v = row?.[f.key];
      if (f.type === "file")
        out[f.key] = Array.isArray(v) ? (v as { key: string; filename: string }[]) : [];
    }
    return out;
  });
  const recordId = typeof row?.id === "string" ? row.id : null;

  const save = async () => {
    const missing = missingRequired(entity, draft);
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map((k) => [k, "필수 항목입니다"])));
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const body = payloadOf(entity, draft, original);
      if (recordId) await updateErpRecord(entity.key, recordId, body);
      else await createErpRecord(entity.key, body);
      invalidateErpOptions(entity.key);
      toast(recordId ? "저장했습니다" : "등록했습니다");
      onSaved();
    } catch (e) {
      const fe = fieldErrors(e);
      if (Object.keys(fe).length) setErrors(fe);
      else toast((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!recordId) return;
    setSaving(true);
    try {
      await deleteErpRecord(entity.key, recordId);
      invalidateErpOptions(entity.key);
      toast("삭제했습니다");
      setConfirmDelete(false);
      onSaved();
    } catch (e) {
      // 409 = 다른 레코드가 참조 중 (cascade 하지 않는다)
      toast((e as Error).message, "error");
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const fields = editableFields(entity);
  const fileFields = entity.fields.filter((f) => f.type === "file");
  const derived = entity.fields.filter((f) => f.readonly && row);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={`${entity.title} ${recordId ? "수정" : "등록"}`}
        width={720}
        footer={
          <>
            {recordId && canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                icon={<Icon name="trash" size={14} />}
              >
                삭제
              </Button>
            )}
            <span className="flex-1" />
            <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save()}
              loading={saving}
              icon={<Icon name="save" size={14} />}
            >
              저장
            </Button>
          </>
        }
      >
        <div className="grid max-h-[65vh] grid-cols-1 gap-s4 overflow-y-auto pr-s2 md:grid-cols-2">
          {fields.map((f) =>
            f.type === "bool" ? (
              <div key={f.key} className="flex items-end">
                <FieldControl
                  f={f}
                  value={draft[f.key]}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                  p={{ id: `erp-${f.key}` }}
                />
              </div>
            ) : (
              <Field
                key={f.key}
                label={f.unit ? `${f.label} (${f.unit})` : f.label}
                required={f.required && !f.auto}
                hint={f.help || undefined}
                error={errors[f.key]}
                className={f.type === "longtext" ? "md:col-span-2" : undefined}
              >
                {(p) => (
                  <FieldControl
                    f={f}
                    value={draft[f.key]}
                    onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                    p={p}
                  />
                )}
              </Field>
            )
          )}
          {derived.map((f) => (
            <Field key={f.key} label={f.unit ? `${f.label} (${f.unit})` : f.label} hint="자동 계산">
              {(p) => (
                <TextInput
                  value={String(row?.[f.key] ?? "")}
                  disabled
                  align={isNumeric(f.type) ? "right" : "left"}
                  {...p}
                />
              )}
            </Field>
          ))}
          {fileFields.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              hint={recordId ? undefined : "저장한 뒤 첨부할 수 있습니다"}
              className="md:col-span-2"
            >
              {recordId ? (
                <FileList
                  entity={entity.key}
                  recordId={recordId}
                  f={f}
                  files={files[f.key] ?? []}
                  onChanged={(next) => setFiles((x) => ({ ...x, [f.key]: next }))}
                  onError={(m) => toast(m, "error")}
                />
              ) : (
                <span className="text-caption" style={{ color: "var(--text-quaternary)" }}>
                  -
                </span>
              )}
            </Field>
          ))}
          {errors[""] && (
            <p className="text-caption md:col-span-2" style={{ color: "var(--state-late)" }}>
              {errors[""]}
            </p>
          )}
        </div>
      </Dialog>
      <ConfirmDialog
        open={confirmDelete}
        title={`${entity.title} 삭제`}
        message="삭제하면 되돌릴 수 없습니다. 다른 기록이 참조하고 있으면 삭제되지 않습니다."
        confirmLabel="삭제"
        busy={saving}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export { RefSelect };
